import React, { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiPlus, FiMinus, FiCheck } from 'react-icons/fi';
import api from '../services/api';
import '../components/Button.css';
import { RETOUR_REDEN_OPTIES, RETOUR_REDEN_VALUE_ANDERS } from '../constants/retourenReden';
import './RetourAanmelden.css';

function opmerkingenPubliek(redenKey, redenAnders, opmerkingKlant) {
  const opt = (opmerkingKlant || '').trim();
  if (redenKey === RETOUR_REDEN_VALUE_ANDERS) {
    const a = (redenAnders || '').trim();
    if (!a) return opt;
    return opt ? `${a}\n\n${opt}` : a;
  }
  return opt;
}

const formatEuro = (n) => {
  const v = parseFloat(n);
  if (!Number.isFinite(v)) return '€ 0,00';
  return `€ ${v.toFixed(2).replace('.', ',')}`;
};

const formatDatum = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Fase 4: openbaar retourformulier (zonder inlog) — zelfde validatie als
 * /retouren/lookup?verificatie=klant en POST /retouren/publiek.
 */
const EMBED_RESIZE = 'inkoop-retour-resize';

const RetourAanmelden = () => {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === '1' || String(searchParams.get('embed') || '').toLowerCase() === 'true';
  const pageClass = `retour-publiek-page${isEmbed ? ' retour-publiek-page--embed' : ''}`;
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isEmbed) return undefined;
    const h = document.documentElement;
    const b = document.body;
    const r = document.getElementById('root');
    h.classList.add('inkoop-retour-embed');
    b.classList.add('inkoop-retour-embed');
    if (r) r.classList.add('inkoop-retour-embed');
    return () => {
      h.classList.remove('inkoop-retour-embed');
      b.classList.remove('inkoop-retour-embed');
      if (r) r.classList.remove('inkoop-retour-embed');
    };
  }, [isEmbed]);

  const postEmbedHeight = useCallback(() => {
    if (!isEmbed || !window.parent || window.parent === window) return;
    const el = document.documentElement;
    const b = document.body;
    const raw = Math.max(el.scrollHeight, b?.scrollHeight || 0, b?.getBoundingClientRect().height || 0);
    const h = Math.ceil(raw) + 12;
    if (h > 0) {
      window.parent.postMessage({ type: EMBED_RESIZE, height: h }, '*');
    }
  }, [isEmbed]);

  const [stap, setStap] = useState('form'); // 'form' | 'items' | 'bedankt'
  const [ordernummer, setOrdernummer] = useState('');
  const [email, setEmail] = useState('');

  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState('');

  const [order, setOrder] = useState(null);
  const [selected, setSelected] = useState({}); // { wc_line_item_id: aantal }
  const [redenKey, setRedenKey] = useState('');
  const [redenAnders, setRedenAnders] = useState('');
  const [opmerkingKlant, setOpmerkingKlant] = useState('');

  const [opslaan, setOpslaan] = useState(false);
  const [bevestig, setBevestig] = useState({ id: null, retournummer: null });

  useLayoutEffect(() => {
    if (!isEmbed) return undefined;
    const ro = new ResizeObserver(() => {
      postEmbedHeight();
    });
    if (rootRef.current) {
      ro.observe(rootRef.current);
    }
    postEmbedHeight();
    return () => ro.disconnect();
  }, [isEmbed, postEmbedHeight, stap]);

  useLayoutEffect(() => {
    if (!isEmbed) return;
    const t = window.setTimeout(() => postEmbedHeight(), 100);
    return () => window.clearTimeout(t);
  }, [stap, order, fout, laden, opslaan, bevestig, isEmbed, postEmbedHeight]);

  const retourTotaal = useMemo(() => {
    if (!order) return 0;
    return (order.regels || []).reduce((s, r) => {
      const a = selected[r.wc_line_item_id] || 0;
      return s + a * (r.prijs_per_stuk || 0);
    }, 0);
  }, [order, selected]);

  const resetForm = useCallback(() => {
    setStap('form');
    setOrder(null);
    setSelected({});
    setRedenKey('');
    setRedenAnders('');
    setOpmerkingKlant('');
    setFout('');
    setBevestig({ id: null, retournummer: null });
  }, []);

  const zoekOrder = async (e) => {
    e.preventDefault();
    setFout('');
    if (!ordernummer.trim() || !email.trim()) {
      setFout('Vul ordernummer en e-mail in (e-mail = factuur in webshop).');
      return;
    }
    setLaden(true);
    try {
      const res = await api.get(`/retouren/lookup-order/${encodeURIComponent(ordernummer.trim())}`, {
        params: { email: email.trim(), verificatie: 'klant' },
      });
      setOrder(res.data);
      setSelected(
        (res.data.regels || []).reduce(
          (acc, r) => {
            acc[r.wc_line_item_id] = 0;
            return acc;
          },
          {}
        )
      );
      setStap('items');
    } catch (err) {
      setFout(
        err.response?.data?.error || err.message || 'Order kon niet worden gevonden of e-mail past niet.'
      );
    } finally {
      setLaden(false);
    }
  };

  const bumpAantal = (lineId, max, delta) => {
    setSelected((prev) => {
      const c = (prev[lineId] || 0) + delta;
      const n = Math.max(0, Math.min(max, c));
      return { ...prev, [lineId]: n };
    });
  };

  const verzenden = async (e) => {
    e.preventDefault();
    if (retourTotaal <= 0) {
      setFout('Kies per product minimaal één stuk om te retouren.');
      return;
    }
    if (!redenKey) {
      setFout('Kies een retourreden in de lijst.');
      return;
    }
    if (redenKey === RETOUR_REDEN_VALUE_ANDERS && !redenAnders.trim()) {
      setFout('Bij "Anders" is een toelichting verplicht.');
      return;
    }
    const regels = (order.regels || [])
      .map((r) => ({
        wc_line_item_id: r.wc_line_item_id,
        aantal_aangemeld: selected[r.wc_line_item_id] || 0,
      }))
      .filter((x) => x.aantal_aangemeld > 0);
    if (regels.length === 0) {
      setFout('Geen geldige regel.');
      return;
    }
    setOpslaan(true);
    setFout('');
    try {
      const res = await api.post('/retouren/publiek', {
        ordernummer: order.order_nummer,
        klant_email: email.trim(),
        regels,
        reden: redenKey,
        opmerkingen_klant: opmerkingenPubliek(redenKey, redenAnders, opmerkingKlant),
        opmerkingen_intern: '',
      });
      if (res.data?.success) {
        setBevestig({ id: res.data.id, retournummer: res.data.retournummer });
        setStap('bedankt');
      } else {
        setFout('Onverwacht antwoord van de server');
      }
    } catch (err) {
      setFout(err.response?.data?.error || err.message || 'Kon retour niet indienen.');
    } finally {
      setOpslaan(false);
    }
  };

  if (stap === 'bedankt') {
    return (
      <div ref={rootRef} className={pageClass}>
        <div className="retour-publiek-card retour-publiek-card--thx">
          <h1>Retour melding ontvangen</h1>
          {bevestig?.retournummer && (
            <p className="retour-publiek-thx-rnummer">
              <strong>Retournummer:</strong> {bevestig.retournummer}
            </p>
          )}
          <div className="retour-publiek-thx-opmerking">
            <strong>Let op:</strong>
            <ul>
              <li>Verstuur je product in originele verpakking retour.</li>
              <li>Schrijf niet op de dozen.</li>
              <li>Plak je eigen verzendlabel duidelijk op de doos; bedek of verwijder het oude verzendlabel waar mogelijk.</li>
              <li>Plak de doos dicht met normaal plakband en niet met duct tape.</li>
              <li>
                Wij verstrekken geen retourlabel. Je regelt de verzending zelf bij een vervoerder
                (bijv. PostNL of DPD). De verzendkosten zijn voor eigen rekening.
              </li>
            </ul>
            <p>Hartelijk dank voor je medewerking.</p>
          </div>
          <p className="retour-publiek-thx-lead">Stuur je pakket retour naar:</p>
          <address className="retour-publiek-thx-addr">
            4Shine.nl
            <br />
            Kelvinring 52C
            <br />
            2952 BG Alblasserdam
            <br />
            Nederland
          </address>
          <p className="retour-publiek-hint">
            Na ontvangst en verwerking van je retour betalen we je direct terug.
          </p>
          <div className="retour-publiek-actions">
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Nog een retour melden
            </button>
          </div>
          <p className="retour-publiek-thx-emailnote">
            We verzenden deze informatie ook direct per e-mail.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={pageClass}>
      <div className="retour-publiek-card">
        <header className="retour-publiek-header">
          <h1>Retour aanmelden</h1>
          <p>Alleen voor bestellingen uit onze webshop, binnen 30 dagen na ontvangst. Je ordernummer-email moet overeenkomen.</p>
        </header>

        {fout && <div className="retour-publiek-fout">{fout}</div>}

        {stap === 'form' && (
          <form onSubmit={zoekOrder} className="retour-publiek-zoek">
            <div className="form-group retour-publiek-field">
              <label htmlFor="rp-ord">Ordernummer (zoals in de e-mail of factuur)</label>
              <input
                id="rp-ord"
                type="text"
                value={ordernummer}
                onChange={(e) => setOrdernummer(e.target.value)}
                required
                autoFocus
                disabled={laden}
                autoComplete="off"
                placeholder="Bijv. 12345 of #12345"
              />
            </div>
            <div className="form-group retour-publiek-field">
              <label htmlFor="rp-mail">E-mail (factuur / bestelling — moet overeenkomen)</label>
              <input
                id="rp-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={laden}
                autoComplete="email"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={laden}>
              {laden ? 'Bezig met zoeken…' : 'Order ophalen'}
            </button>
          </form>
        )}

        {stap === 'items' && order && (
          <form onSubmit={verzenden} className="retour-publiek-winkelmand">
            <p className="retour-publiek-order-meta">
              <strong>Order {order.order_nummer}</strong>
              {order.datum && <span> — {formatDatum(order.datum)}</span>}
            </p>

            <div className="retour-publiek-tabel">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Besteld</th>
                    <th>Aantal retour</th>
                    <th>Deel</th>
                  </tr>
                </thead>
                <tbody>
                  {order.regels.map((r) => {
                    const a = selected[r.wc_line_item_id] || 0;
                    return (
                      <tr key={r.wc_line_item_id}>
                        <td>
                          {r.naam}
                          {r.sku && <div className="retour-publiek-sku">SKU: {r.sku}</div>}
                        </td>
                        <td>{r.aantal}</td>
                        <td>
                          <div className="retour-publiek-stepper">
                            <button
                              type="button"
                              onClick={() => bumpAantal(r.wc_line_item_id, r.aantal, -1)}
                              disabled={a <= 0}
                              aria-label="Eén minder"
                            >
                              <FiMinus />
                            </button>
                            <input
                              type="text"
                              readOnly
                              className="retour-publiek-stap-input"
                              value={a}
                            />
                            <button
                              type="button"
                              onClick={() => bumpAantal(r.wc_line_item_id, r.aantal, 1)}
                              disabled={a >= r.aantal}
                              aria-label="Eén meer"
                            >
                              <FiPlus />
                            </button>
                          </div>
                        </td>
                        <td className="retour-publiek-som">{formatEuro((a * r.prijs_per_stuk) || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="retour-publiek-total-label">
                      <strong>Terugbetalen (incl. btw) indicatie</strong>
                    </td>
                    <td>
                      <strong>{formatEuro(retourTotaal)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-group retour-publiek-field">
              <label htmlFor="rp-reden">Reden retour *</label>
              <select
                id="rp-reden"
                value={redenKey}
                onChange={(e) => setRedenKey(e.target.value)}
                required
                disabled={opslaan}
              >
                <option value="">— Kies een reden —</option>
                {RETOUR_REDEN_OPTIES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {redenKey === RETOUR_REDEN_VALUE_ANDERS && (
              <div className="form-group retour-publiek-field">
                <label htmlFor="rp-reden-anders">Toelichting bij &quot;Anders&quot; *</label>
                <textarea
                  id="rp-reden-anders"
                  value={redenAnders}
                  onChange={(e) => setRedenAnders(e.target.value)}
                  rows="3"
                  required
                  disabled={opslaan}
                  placeholder="Beschrijf kort de reden…"
                />
              </div>
            )}
            <div className="form-group retour-publiek-field">
              <label htmlFor="rp-note">Toelichting (optioneel)</label>
              <textarea
                id="rp-note"
                value={opmerkingKlant}
                onChange={(e) => setOpmerkingKlant(e.target.value)}
                rows="2"
                disabled={opslaan}
                placeholder="Extra informatie voor ons (bij elke geselecteerde reden mogelijk)"
              />
            </div>

            <div className="retour-publiek-form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
                disabled={opslaan}
              >
                Ander ordernummer
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  opslaan
                  || retourTotaal <= 0
                  || !redenKey
                  || (redenKey === RETOUR_REDEN_VALUE_ANDERS && !redenAnders.trim())
                }
              >
                {opslaan ? 'Indienen…' : 'Retour definitief indienen'}
                {!opslaan && <FiCheck className="retour-publiek-ico" />}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RetourAanmelden;
